/**
 * Push Notifications API Service
 * Handles communication with the backend for push notification subscriptions
 */

import api from './api';
import {
  PushSubscriptionData,
  PushSubscriptionRecord,
  PushSubscriptionResponse,
  VapidPublicKeyResponse,
  TestNotificationRequest,
  getDeviceInfo,
} from '../types/pushNotification';

// Route contract (DRIFT-2): the shared `api` client prefixes /api/v1, and
// WebPushController ships on [Route("api/v{version}/push")] → /api/v1/push/*.
// The previous `/notifications/push/*` prefix matched no controller and 404'd,
// so web push was entirely dead. Verb/shape drift is also corrected below to
// match the SHIPPED actions: GET vapid-public-key, POST subscribe,
// DELETE unsubscribe (?endpoint=), GET subscriptions, POST test,
// GET subscription-status. There is intentionally no by-id subscription
// removal/rename endpoint on the backend.

/**
 * Push Notifications API endpoints
 */
export const pushNotificationsApi = {
  /**
   * Get the server's VAPID public key for push subscription
   * This key is used to encrypt push messages
   */
  getVapidPublicKey: async (): Promise<string> => {
    try {
      const response = await api.get<VapidPublicKeyResponse>(
        '/push/vapid-public-key'
      );
      return response.data.publicKey;
    } catch (error) {
      console.error('[PushAPI] Failed to get VAPID public key:', error);
      // Return a fallback key for development (replace with your actual key in production)
      // This is a placeholder - in production, this should always come from the server
      return 'BEl62iUYgUivxIkv69yViEuiBIa40HI80NmELGMqcRuebcdLQOBdlIJ2_Z_lZf6MZdmBDSaFhj-R0zJ_oYUawcI';
    }
  },

  /**
   * Subscribe to push notifications
   * Saves the push subscription to the server
   */
  subscribe: async (
    subscription: PushSubscription,
    deviceName?: string
  ): Promise<PushSubscriptionResponse> => {
    const subscriptionJson = subscription.toJSON();
    const deviceInfo = getDeviceInfo();

    const requestBody = {
      subscription: {
        endpoint: subscriptionJson.endpoint,
        expirationTime: subscriptionJson.expirationTime || null,
        keys: {
          p256dh: subscriptionJson.keys?.p256dh || '',
          auth: subscriptionJson.keys?.auth || '',
        },
      } as PushSubscriptionData,
      deviceName: deviceName || deviceInfo.name,
      userAgent: navigator.userAgent,
      browser: deviceInfo.browser,
      platform: deviceInfo.platform,
      timestamp: new Date().toISOString(),
    };

    const response = await api.post<PushSubscriptionResponse>(
      '/push/subscribe',
      requestBody
    );
    return response.data;
  },

  /**
   * Unsubscribe from push notifications
   * Removes the push subscription from the server.
   * Backend contract: DELETE /push/unsubscribe?endpoint=... (endpoint is [FromQuery]).
   */
  unsubscribe: async (endpoint: string): Promise<PushSubscriptionResponse> => {
    const response = await api.delete<PushSubscriptionResponse>(
      '/push/unsubscribe',
      { params: { endpoint } }
    );
    return response.data;
  },

  /**
   * Get all push subscriptions for the current user
   * Returns a list of all devices with active push subscriptions.
   * Backend returns a bare array (List<PushSubscriptionInfoDto>), NOT a
   * { subscriptions } envelope.
   */
  getSubscriptions: async (): Promise<PushSubscriptionRecord[]> => {
    try {
      const response = await api.get<PushSubscriptionRecord[]>(
        '/push/subscriptions'
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[PushAPI] Failed to get subscriptions:', error);
      return [];
    }
  },

  /**
   * Remove a specific device subscription by its server id.
   *
   * NOTE: The backend exposes no by-id removal endpoint — only
   * DELETE /push/unsubscribe?endpoint=. This method therefore degrades to a
   * no-op so the managing UI does not error; callers that hold the endpoint
   * (e.g. usePushNotifications.removeDevice) should prefer `unsubscribe`.
   */
  removeSubscription: async (
    subscriptionId: string
  ): Promise<PushSubscriptionResponse> => {
    console.warn(
      '[PushAPI] removeSubscription is a no-op: the backend has no by-id ' +
        `removal endpoint (id=${subscriptionId}). Use unsubscribe(endpoint) instead.`
    );
    return {
      success: false,
      message: 'Removing a device by id is not supported by the server.',
    };
  },

  /**
   * Send a test notification to the current device
   * Useful for verifying push notifications are working
   */
  sendTestNotification: async (
    options?: TestNotificationRequest
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      '/push/test',
      {
        title: options?.title || 'Test Notification',
        body:
          options?.body ||
          'This is a test notification from BarbrIQ. Push notifications are working correctly!',
        url: options?.url || '/dashboard',
      }
    );
    return response.data;
  },

  /**
   * Check if the current subscription is still active on the server.
   *
   * Backend contract: GET /push/subscription-status returns
   * { hasActiveSubscriptions, subscriptionCount, userId } for the caller — it
   * does not take an endpoint. The `endpoint` arg is retained for call-site
   * compatibility but not sent; hasActiveSubscriptions is mapped to isActive.
   */
  checkSubscriptionStatus: async (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    endpoint: string
  ): Promise<{ isActive: boolean; subscription?: PushSubscriptionRecord }> => {
    try {
      const response = await api.get<{
        hasActiveSubscriptions: boolean;
        subscriptionCount: number;
        userId: string;
      }>('/push/subscription-status');
      return { isActive: !!response.data.hasActiveSubscriptions };
    } catch (error) {
      console.error('[PushAPI] Failed to check subscription status:', error);
      return { isActive: false };
    }
  },
};

/**
 * Convert VAPID public key from base64 to Uint8Array
 * Required for PushManager.subscribe()
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Check if push notifications are supported in the current browser
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get the current notification permission state
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[PushAPI] Notifications not supported in this browser');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

/**
 * Get the current push subscription from the browser
 */
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('[PushAPI] Error getting current subscription:', error);
    return null;
  }
}

/**
 * Create a new push subscription in the browser
 */
export async function createPushSubscription(
  vapidPublicKey: string
): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    console.warn('[PushAPI] Push notifications not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      return existingSubscription;
    }

    // Create new subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    return subscription;
  } catch (error) {
    console.error('[PushAPI] Error creating push subscription:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications in the browser
 */
export async function removePushSubscription(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const result = await subscription.unsubscribe();
      return result;
    }

    return true;
  } catch (error) {
    console.error('[PushAPI] Error unsubscribing:', error);
    return false;
  }
}

export default pushNotificationsApi;
